import React, { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useBannerOffset } from "@hooks/useBannerOffset";

import { bannerSurface } from "@utils/bannerGradient";

import { ERoutes } from "@/utils/constants";

/**
 * Two site-wide blue notices for the beta. They are separate things that happen
 * to share a strip, and the placement decides which one you get — not the route.
 *
 * **The welcome** — at the TOP of every page: "Welcome to the new Audio Sanctum
 * (Read More)", linking to `/updates`, the running list of what has changed.
 * Dismissible, and dismissing it means gone. For a customer of the old site,
 * "new" raises the question of what happened to their account and their music,
 * so the notice has somewhere to send them; once they have read it, it has done
 * its job and should stop taking a strip off the top of every page. The text
 * itself is the link rather than a button beside it: this strip is one line at
 * every width down to 360px.
 *
 * **The ask** — ALWAYS above the footer, on every page, with no dismissal:
 * "Make Audio Sanctum better" and a Report-a-bug button. Not something a reader
 * clears, because it is not about them — it is a standing invitation for as long
 * as the beta lasts, and the end of a visit is when someone has actually hit
 * something worth reporting. It asks rather than announces; "Audio Sanctum is
 * currently in beta" is a statement about us and gives the reader nothing to do.
 * The icon is a bug, not an envelope: the button is not "write to us", it is
 * "report the thing you just hit".
 *
 * These used to be ONE notice that MOVED: dismissing the welcome from the header
 * republished it above the footer as the ask. That made the bug report reachable
 * only for people who had dismissed the welcome, and made the welcome
 * undismissable in the ordinary sense — two unrelated jobs sharing one flag.
 * They are now independent: the welcome's dismissal hides the welcome and
 * nothing else, and the ask is simply always there.
 *
 * Blue on purpose. Every other banner in this family means something is wrong,
 * needs attention, or went well; these mean none of those. They read as
 * information rather than as a state to resolve.
 *
 * Same mechanics as the rest of the family: the same 360ms collapse, and the top
 * placement publishes its height into `--beta-banner-h` so the fixed header's
 * offset reserves the strip instead of letting content slide behind. The bottom
 * placement sits in normal flow above the footer and reserves nothing — there is
 * nothing below it to push.
 */
const DISMISS_KEY = "beta-banner-dismissed-v1";

/** Pages that will not carry the WELCOME at the top. The claims page opens on
    its own stack — account issue banners, the channel picker, the tab bar — and
    a strip above all of that pushes the thing someone came for below the fold.
    The ask above the footer is unaffected. */
const NO_TOP_ROUTES: string[] = [ERoutes.AccountVideos];

/** Pages that carry NEITHER notice.
 *
 *  The order-success page is the receipt for money that has just left someone's
 *  card. It has one job — tell them the purchase worked and what they now have —
 *  and a site-wide strip about the beta reads as a warning attached to the thing
 *  they just bought. Matched by prefix, because the page is reached both as
 *  `/order/success/:id` and as `/order/success?pi=…`. */
const NO_BANNER_PREFIXES: string[] = [ERoutes.OrderSuccess];

const bannerIsUnwelcome = (pathname: string) =>
  NO_BANNER_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const BetaGlobalBanner: React.FC<{ placement?: "top" | "bottom" }> = ({ placement = "top" }) => {
  // Read synchronously, before the first paint. Starting `false` and correcting
  // in an effect would flash the welcome for a frame on every page load for
  // somebody who dismissed it weeks ago.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [fadingOut, setFadingOut] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const atBottom = placement === "bottom";
  const { pathname } = useLocation();
  // Placement decides the message. The bottom strip is always the ask and the
  // top strip is always the welcome — they are two different notices, not one
  // notice in two moods, so nothing about the route changes which you get.
  const asks = atBottom;

  const showBanner = bannerIsUnwelcome(pathname)
    ? false
    // The ask is permanent: no flag, no dismissal, every page.
    : atBottom || (!dismissed && !NO_TOP_ROUTES.includes(pathname));

  // Only the top placement is out of flow and needs its strip reserved.
  useBannerOffset("--beta-banner-h", showBanner && !atBottom, bannerRef);

  if (!showBanner) return null;

  // Only the welcome has one. Remembered against a version key, so a later beta
  // phase can raise the notice again without having to guess which browsers
  // still hold a stale flag.
  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch { /* non-fatal — the notice simply returns on the next visit */ }
    setFadingOut(true);
    window.setTimeout(() => {
      setDismissed(true);
      setFadingOut(false);
    }, 360);
  };

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      className="beta-banner"
      style={{
        width: "100%",
        ...bannerSurface("rgb(41, 98, 155)"),
        // Both notices are a single line at every width. Everything that takes
        // horizontal room — the side padding, the gap, the two type sizes, the
        // X's inset — is a clamp rather than a fixed value, because these are
        // inline styles and cannot carry a media query. They hold their design
        // sizes from roughly 410px up and only give ground on the narrowest
        // phones, where a wrap would otherwise put the icon on its own line.
        padding: fadingOut ? "0 clamp(10px, 3vw, 24px)" : "12px clamp(10px, 3vw, 24px)",
        marginTop: atBottom ? 0 : -1,
        maxHeight: fadingOut ? 0 : 200,
        opacity: fadingOut ? 0 : 1,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "nowrap",
        gap: "clamp(6px, 2vw, 12px)",
        color: "#fff",
        fontFamily: '"Raleway", sans-serif',
        fontSize: "0.875rem",
        position: "relative",
        transition:
          "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {!asks && (
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="#ffd75e"
          stroke="none"
          style={{ flexShrink: 0, marginRight: -4 }}
          aria-hidden="true"
        >
          <path d="M12 2.8 14.6 8.7 21 9.4l-4.8 4.3 1.3 6.3L12 16.9 6.5 20l1.3-6.3L3 9.4l6.4-.7Z" />
        </svg>
      )}
      {asks ? (
        <span
          style={{
            fontWeight: 500,
            whiteSpace: "nowrap",
            fontSize: "clamp(0.72rem, 3.1vw, 0.875rem)",
          }}
        >
          Make Audio Sanctum better
        </span>
      ) : (
        // The welcome is the LINK, not a button beside it. The ask already
        // spends the row's remaining width on "Report a bug", and a second
        // control on the welcome would have to survive the same single line at
        // 360px. Underlined so it reads as a link on a strip nothing else on
        // the site is clickable in.
        <Link
          to={ERoutes.WhatsNew}
          style={{
            fontWeight: 500,
            whiteSpace: "nowrap",
            fontSize: "clamp(0.75rem, 3.4vw, 0.875rem)",
            color: "#fff",
            textDecoration: "underline",
            textUnderlineOffset: 3,
            textDecorationColor: "rgba(255, 255, 255, 0.45)",
            textDecorationThickness: 1,
            transition: "text-decoration-color 0.2s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.textDecorationColor = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.textDecorationColor = "rgba(255, 255, 255, 0.45)"; }}
        >
          {/* Both wordings, one hidden by CSS at 600px (`index.css`). Chosen
              there rather than here because the banner is prerendered into
              every page at build time: a JavaScript width check would bake the
              desktop sentence into the HTML and a phone would wear it until the
              app hydrates. Only "Welcome to" is dropped — "new" is the word
              that makes a returning customer tap. */}
          <span className="beta-copy-long">Welcome to the new Audio Sanctum (Read More)</span>
          <span className="beta-copy-short">The new Audio Sanctum (Read More)</span>
        </Link>
      )}
      {asks && (
      <Link
        to={ERoutes.Contact}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "clamp(4px, 1.6vw, 7px)",
          padding: "2px 10px",
          borderRadius: 6,
          background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
          border: "none",
          color: "#2a1d14",
          flexShrink: 0,
          fontFamily: '"Raleway", sans-serif',
          fontSize: "clamp(0.68rem, 2.9vw, 0.8125rem)",
          fontWeight: 600,
          textDecoration: "none",
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: "0 0 12px rgba(186, 144, 92, 0.25)",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(186, 144, 92, 0.5)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 0 12px rgba(186, 144, 92, 0.25)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="m8 2 1.88 1.88" />
          <path d="M14.12 3.88 16 2" />
          <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
          <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
          <path d="M12 20v-9" />
          <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
          <path d="M6 13H2" />
          <path d="M6.53 17c-1.93.2-3.53 1.9-3.53 4" />
          <path d="M17.47 9c1.93-.2 3.53-1.9 3.53-4" />
          <path d="M18 13h4" />
          <path d="M17.47 17c1.93.2 3.53 1.9 3.53 4" />
        </svg>
        Report a bug
      </Link>
      )}
      {!atBottom && (
      <button
        onClick={handleDismiss}
        aria-label="Move to the bottom of the page"
        className="banner-dismiss"
        style={{
          position: "absolute",
          top: "50%",
          right: "clamp(4px, 2vw, 16px)",
          transform: "translateY(-50%)",
          width: 32,
          height: 32,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.98)",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color 0.15s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.98)"; }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      )}
    </div>
  );
};

export default BetaGlobalBanner;
